import React, { useState, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { User, Users, Coins, ChevronLeft, ChevronRight } from 'lucide-react';
import { useCreditBalance, useCreditHistory } from '@/services/credits/hooks';
import { queryKeys } from '@/api/queryKeys';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

interface CreditsDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

const CreditsDialog: React.FC<CreditsDialogProps> = ({ isOpen, onClose }) => {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const pageSize = 10;

  const { data: balance } = useCreditBalance();
  const { data: historyData, isLoading, error } = useCreditHistory(page, pageSize);

  const transactions = historyData?.transactions ?? [];
  const total = historyData?.total ?? 0;
  const totalPages = Math.ceil(total / pageSize);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (open) {
        queryClient.invalidateQueries({ queryKey: queryKeys.credits.all });
      }
      if (!open) onClose();
    },
    [queryClient, onClose]
  );

  const getTransactionIcon = (type: string) => {
    switch (type) {
      case 'deduction':
        return '−';
      case 'refund':
        return '↩';
      case 'purchase':
      case 'admin_adjustment':
        return '+';
      default:
        return '•';
    }
  };

  const getTransactionColor = (type: string) => {
    switch (type) {
      case 'deduction':
        return 'text-destructive';
      case 'refund':
      case 'purchase':
      case 'admin_adjustment':
        return 'text-green-600 dark:text-green-400';
      default:
        return 'text-muted-foreground';
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  };

  const formatTransactionType = (type: string) => {
    return type
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-[600px] max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Coins className="size-5" />
            Credits
          </DialogTitle>
        </DialogHeader>

        {/* Balance Summary */}
        <div className="space-y-3">
          {/* Total Available Card */}
          <div className="p-4 rounded-lg bg-primary text-primary-foreground text-center">
            <div className="text-sm opacity-90">Total Available</div>
            <div className="text-3xl font-bold">{balance?.totalAvailable ?? 0}</div>
            <div className="text-xs opacity-75">Credits</div>
          </div>

          {/* Personal & Team Cards */}
          <div className="grid grid-cols-2 gap-3">
            <div className="flex items-center gap-3 p-3 rounded-lg border border-border bg-card">
              <div className="p-2 rounded-full bg-blue-100 text-blue-600 dark:bg-blue-900 dark:text-blue-300">
                <User className="size-4" />
              </div>
              <div>
                <div className="text-xs text-muted-foreground">Personal</div>
                <div className="text-lg font-semibold">{balance?.userBalance ?? 0}</div>
              </div>
            </div>

            <div className="flex items-center gap-3 p-3 rounded-lg border border-border bg-card">
              <div className="p-2 rounded-full bg-purple-100 text-purple-600 dark:bg-purple-900 dark:text-purple-300">
                <Users className="size-4" />
              </div>
              <div>
                <div className="text-xs text-muted-foreground">Team</div>
                <div className="text-lg font-semibold">{balance?.teamBalance ?? 0}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Transaction History */}
        <div className="flex-1 overflow-hidden flex flex-col mt-4">
          <h3 className="text-sm font-semibold mb-2">Transaction History</h3>

          {error && (
            <div className="p-3 text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded-md">
              {error.message}
            </div>
          )}

          {isLoading ? (
            <div className="flex-1 flex items-center justify-center text-muted-foreground">
              Loading transactions...
            </div>
          ) : transactions.length === 0 ? (
            <div className="flex-1 flex items-center justify-center text-muted-foreground">
              No transactions found
            </div>
          ) : (
            <>
              <div className="flex-1 overflow-y-auto space-y-2 pr-1">
                {transactions.map((transaction) => (
                  <div
                    key={transaction.id}
                    className="flex items-start gap-3 p-3 rounded-md border border-border bg-card hover:bg-accent/50 transition-colors"
                  >
                    <div
                      className={`flex items-center justify-center size-8 rounded-full text-sm font-bold ${getTransactionColor(
                        transaction.transactionType ?? ''
                      )} bg-muted`}
                    >
                      {getTransactionIcon(transaction.transactionType ?? '')}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">{transaction.description}</div>
                      <div className="flex flex-wrap gap-2 mt-1 text-xs text-muted-foreground">
                        <Badge variant="secondary" className="text-xs">
                          {formatTransactionType(transaction.transactionType ?? '')}
                        </Badge>
                        {transaction.teamId && (
                          <span title="Team context at time of transaction">
                            Team: {transaction.teamId.substring(0, 8)}...
                          </span>
                        )}
                        <span>{formatDate(transaction.createdAt)}</span>
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <div
                        className={`text-sm font-semibold ${getTransactionColor(
                          transaction.transactionType ?? ''
                        )}`}
                      >
                        {transaction.amount >= 0 ? '+' : ''}
                        {transaction.amount}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        Balance: {transaction.balanceAfter}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {totalPages > 1 && (
                <div className="flex items-center justify-between pt-3 mt-3 border-t border-border">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page === 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                  >
                    <ChevronLeft className="size-4" />
                    Previous
                  </Button>
                  <span className="text-sm text-muted-foreground">
                    Page {page} of {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page === totalPages}
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  >
                    Next
                    <ChevronRight className="size-4" />
                  </Button>
                </div>
              )}
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CreditsDialog;
